import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { ProgressSpinner } from "./shared/ui/feedback/progress-spinner/progress-spinner";
import { Loading } from './core/services/loading';
import { DateAdapter } from '@angular/material/core';
import { TranslocoService } from '@jsverse/transloco';
import { Locale } from 'date-fns/locale';
import { it } from 'date-fns/locale/it';
import { enUS } from 'date-fns/locale/en-US';
import { Auth } from './core/services/auth';
import { PwaInstallPromptComponent } from './core/pwa/components/pwa-install-prompt/pwa-install-prompt';
import { PwaUpdatePromptComponent } from './core/pwa/components/pwa-update-prompt/pwa-update-prompt';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ProgressSpinner, PwaInstallPromptComponent, PwaUpdatePromptComponent],
  templateUrl: './app.html',
  styleUrl: './app.css'
})
export class App {
  protected readonly title = signal('authds-web');
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly dateAdapter = inject(DateAdapter);
  private readonly destroyRef = inject(DestroyRef);
  private readonly localeMap: Record<string, Locale> = { it, en: enUS };

  private readonly authState = inject(Auth);
  private readonly loading = inject(Loading);

  constructor() {
    this.syncMaterialLocale();
    this.bootstrapAuth();
  }

  private bootstrapAuth(): void {
    this.loading.show();

    this.authState.bootstrapAuth().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.loading.hide();
        void this.handleBootstrapNavigation();
      },
      error: () => {
        this.loading.hide();
      }
    });
  }

  private async handleBootstrapNavigation(): Promise<void> {
    const currentPath = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const isDashboardEntry = currentPath === '/dashboard' || currentPath === '/' || currentPath === '';
    const isOidcCallback = currentPath === '/dashboard' && searchParams.has('code') && searchParams.has('state');

    if (this.authState.isResolving()) {
      return;
    }

    if (this.authState.hasProfileError()) {
      if (currentPath !== '/profile-error') {
        await this.router.navigateByUrl('/profile-error');
      }
      return;
    }

    if (this.authState.isAuthenticated()) {
      if (isDashboardEntry || isOidcCallback) {
        await this.router.navigateByUrl(this.authState.consumePostLoginTargetUrl());
      }
      return;
    }

    if (this.authState.isAnonymous() && !isOidcCallback && isDashboardEntry) {
      this.authState.startLogin();
    }
  }

  private syncMaterialLocale(): void {
    this.setMaterialLocale(this.transloco.getActiveLang());

    this.transloco.langChanges$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(lang => {
      this.setMaterialLocale(lang);
    });
  }

  private setMaterialLocale(lang: string): void {
    this.dateAdapter.setLocale(this.localeMap[lang] ?? it);
  }
}
